import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, PERSIST_STORAGE_KEY } from "../../../store";
import { clearTokensAction } from "../../../store/auth/authState";

/**
 * Cross-tab auth synchronisation. redux-persist writes auth state to
 * localStorage but does not react to changes made in OTHER tabs. This hook
 * listens for `storage` events and, when another tab clears the access token
 * (logout), logs this tab out too — so a sign-out propagates everywhere.
 */
const readPersistedAccessToken = (raw: string | null): string | null => {
  if (!raw) return null;
  try {
    const root = JSON.parse(raw) as Record<string, string>;
    const auth = root.auth ? JSON.parse(root.auth) : null;
    return auth?.accessToken ?? null;
  } catch {
    return null;
  }
};

export const useMultiTabAuthSync = (): void => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  useEffect(() => {
    // Dispatch the action directly (instead of a `clearTokens` closure that is
    // a new reference every render) so the listener is attached once per token
    // change, not on every render.
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== PERSIST_STORAGE_KEY) return;
      const persistedToken = readPersistedAccessToken(event.newValue);
      // Another tab logged out (token cleared) while this tab still holds one.
      if (!persistedToken && accessToken) {
        dispatch(clearTokensAction());
        navigate("/auth/sign-in", { replace: true });
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [accessToken, dispatch, navigate]);
};

export default useMultiTabAuthSync;
